/**
 * Dameuses : modèle instancié posé sur le relief (tangage et roulis suivent
 * la pente), pivot sur place aux changements de cap (véhicule à chenilles),
 * et la nuit, en sortie : nappe de lumière des phares devant la lame, lampes
 * de travail sur le velours derrière la fraise, gyrophare orange et halo des
 * phares visibles de loin. La position vient du seul mouvement diffusé par le
 * serveur, daté en temps de jeu (voir groomerPose).
 */
import * as THREE from 'three/webgpu';
import { abs, float, pow, sin, smoothstep, time, uv, vec2, vec3 } from 'three/tsl';
import { groomerPose, type GroomerContext, type Heightfield } from '@snowchain/shared';
import { InstancedLodSet, type Frustum } from './instancing';
import { nightUniform, type AssetLibrary } from './assets';
import type { ClientStore } from '../state/store';
import type { Terrain } from './terrain';

/** Positions au sol affichées (sélection au clic, caméra qui suit). */
export const groomerSpots = new Map<number, [number, number]>();

const LENGTH = 8.7;
const MAX = 64;

/** Nappe de lumière posée sur la neige : cône qui s'élargit vers l'avant, plus fort près de la source. */
function poolMaterial(color: THREE.Color, strength: number): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
  m.fog = false;
  // x : -1..1 en travers, y : 0 au bord proche, 1 au bord lointain.
  const p = vec2(uv().x.sub(0.5).mul(2), float(1).sub(uv().y));
  const spread = float(0.35).add(p.y.mul(0.65));
  const across = float(1).sub(smoothstep(0.3, 1, abs(p.x.div(spread))));
  const along = pow(float(1).sub(p.y), float(1.3)).mul(smoothstep(0, 0.12, p.y));
  m.colorNode = vec3(color.r, color.g, color.b).mul(across).mul(along).mul(strength).mul(nightUniform);
  m.polygonOffset = true;
  m.polygonOffsetFactor = -4;
  m.polygonOffsetUnits = -4;
  return m;
}

/** Halo face caméra (dégradé radial) : phares, gyrophare. */
function haloMaterial(color: THREE.Node<'vec3'>): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
  m.fog = false;
  const d = uv().sub(0.5).length().mul(2);
  m.colorNode = color.mul(pow(float(1).sub(smoothstep(0, 1, d)), float(2.5)));
  return m;
}

export class GroomersLayer {
  readonly group = new THREE.Group();
  /** Positions au sol affichées, par dameuse (mêmes données que groomerSpots). */
  readonly spots = groomerSpots;
  private readonly body: InstancedLodSet;
  private readonly front: THREE.InstancedMesh;
  private readonly rear: THREE.InstancedMesh;
  private readonly beacons: THREE.InstancedMesh;
  private readonly flares: THREE.InstancedMesh;
  /** Cap affiché de chaque dameuse (le pivot rattrape le cap du mouvement). */
  private readonly yaw = new Map<number, { yaw: number; frame: number }>();
  private readonly mat = new Float32Array(16);
  private readonly m4 = new THREE.Matrix4();
  private readonly pose = [0, 0, 0];
  private readonly vF = new THREE.Vector3();
  private readonly vR = new THREE.Vector3();
  private readonly vU = new THREE.Vector3();
  private readonly vP = new THREE.Vector3();
  private readonly vS = new THREE.Vector3();
  private frame = 0;
  drawn = 0;

  constructor(
    lib: AssetLibrary,
    private readonly hf: Heightfield,
  ) {
    this.group.name = 'groomers';
    this.body = new InstancedLodSet('groomer', this.group, lib.get('groomer').geometries, lib.instanced, 16, [280, 2600], { shadowLods: 1, dynamic: true });
    // Nappes : plan couché, bord proche à l'origine, étiré vers l'avant (+z local).
    const pool = (w: number, l: number) => new THREE.PlaneGeometry(w, l).rotateX(-Math.PI / 2).translate(0, 0, l / 2);
    this.front = new THREE.InstancedMesh(pool(22, 32), poolMaterial(new THREE.Color(1, 0.88, 0.68), 0.6), MAX);
    this.rear = new THREE.InstancedMesh(pool(14, 9), poolMaterial(new THREE.Color(0.8, 0.88, 1), 0.3), MAX);
    // Gyrophare : deux éclats orange par seconde ; halo des phares chaud, la nuit seulement.
    const pulse = pow(sin(time.mul(Math.PI * 4)).mul(0.5).add(0.5), float(3));
    this.beacons = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), haloMaterial(vec3(1, 0.42, 0.04).mul(pulse.mul(2.4).add(0.12))), MAX);
    this.flares = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), haloMaterial(vec3(1, 0.92, 0.78).mul(nightUniform).mul(1.4)), MAX);
    for (const m of [this.front, this.rear, this.beacons, this.flares]) {
      m.count = 0;
      m.frustumCulled = false;
      m.castShadow = false;
      m.receiveShadow = false;
      m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      this.group.add(m);
    }
    this.front.renderOrder = 2;
    this.rear.renderOrder = 2;
  }

  /**
   * Repère posé sur le relief autour de (x, z) : avant selon le cap, pente
   * mesurée sur ±halfL en long et ±halfW en travers. Remplit this.m4 et les
   * vecteurs vR, vU, vF ; l'origine est le centre, relevé de `lift`.
   */
  private ground(x: number, z: number, yaw: number, halfL: number, halfW: number, lift: number): void {
    const fx = Math.sin(yaw);
    const fz = Math.cos(yaw);
    const rx = Math.cos(yaw);
    const rz = -Math.sin(yaw);
    const h = this.hf;
    const hF = h.height(x + fx * halfL, z + fz * halfL);
    const hB = h.height(x - fx * halfL, z - fz * halfL);
    const hR = h.height(x + rx * halfW, z + rz * halfW);
    const hL = h.height(x - rx * halfW, z - rz * halfW);
    this.vF.set(fx * 2 * halfL, hF - hB, fz * 2 * halfL).normalize();
    this.vR.set(rx * 2 * halfW, hR - hL, rz * 2 * halfW).normalize();
    this.vU.crossVectors(this.vF, this.vR).normalize();
    this.vR.crossVectors(this.vU, this.vF).normalize();
    const y = Math.max((hF + hB + hR + hL) / 4, h.height(x, z)) + lift;
    this.m4.makeBasis(this.vR, this.vU, this.vF).setPosition(x, y, z);
  }

  /** Nappe de longueur 2·halfL dont le bord proche est à `near` m devant la dameuse (derrière si back). */
  private pool(x: number, z: number, yaw: number, near: number, halfL: number, halfW: number): void {
    const dir = yaw;
    const cx = x + Math.sin(dir) * (near + halfL);
    const cz = z + Math.cos(dir) * (near + halfL);
    this.ground(cx, cz, dir, halfL, halfW, 0.35);
    this.vP.setFromMatrixPosition(this.m4).addScaledVector(this.vF, -halfL);
    this.m4.setPosition(this.vP);
  }

  update(store: ClientStore, t: number, dt: number, camera: THREE.Camera, frustum: Frustum, terrain: Terrain): void {
    const r = store.replica;
    this.body.begin();
    this.frame++;
    this.drawn = 0;
    groomerSpots.clear();
    let lit = 0;
    if (r) {
      const ctx: GroomerContext = { garage: (id) => r.buildings.get(id), track: (id) => store.pisteTrack(id) };
      const cp = camera.position;
      const camQ = (camera as THREE.PerspectiveCamera).quaternion;
      // Pivot sur place : vitesse angulaire bornée, plus rapide quand le temps est accéléré.
      const turn = 2.4 * Math.max(1, store.clock.speed) * dt;
      for (const g of r.groomers.values()) {
        if (!groomerPose(g.m, t, ctx, this.pose)) continue;
        const x = this.pose[0] as number;
        const z = this.pose[1] as number;
        const target = this.pose[2] as number;
        let s = this.yaw.get(g.id);
        if (!s || s.frame < this.frame - 1) {
          s = { yaw: target, frame: this.frame };
          this.yaw.set(g.id, s);
        }
        let dy = target - s.yaw;
        while (dy > Math.PI) dy -= 2 * Math.PI;
        while (dy < -Math.PI) dy += 2 * Math.PI;
        s.yaw = Math.abs(dy) <= turn ? target : s.yaw + Math.sign(dy) * turn;
        s.frame = this.frame;
        groomerSpots.set(g.id, [x, z]);
        const chunk = terrain.chunkAt(x, z);
        if (chunk && !chunk.visible) continue;
        const yc = this.hf.height(x, z);
        if (!frustum.sphere(x, yc + 2, z, LENGTH)) continue;
        this.ground(x, z, s.yaw, LENGTH / 2 - 0.6, 2.2, -0.05);
        this.m4.toArray(this.mat);
        if (this.body.add(this.mat, 0, Math.hypot(x - cp.x, yc - cp.y, z - cp.z))) this.drawn++;
        if (g.status === 'parked' || lit >= MAX) continue;
        // Repères de la cabine (gyrophare) et de la rampe de phares, dans le repère de la dameuse.
        const up = this.vU.clone();
        const fwd = this.vF.clone();
        const roof = this.vS.setFromMatrixPosition(this.m4).addScaledVector(up, 3.45).addScaledVector(fwd, -0.2).clone();
        const bar = this.vS.setFromMatrixPosition(this.m4).addScaledVector(up, 3.25).addScaledVector(fwd, 1.3).clone();
        this.pool(x, z, s.yaw, 3.6, 16, 11);
        this.front.setMatrixAt(lit, this.m4);
        this.pool(x, z, s.yaw + Math.PI, 4.2, 4.5, 7);
        this.rear.setMatrixAt(lit, this.m4);
        const dist = Math.hypot(bar.x - cp.x, bar.y - cp.y, bar.z - cp.z);
        const size = 3 + Math.min(12, dist * 0.012);
        this.m4.compose(roof, camQ, this.vS.set(size * 0.8, size * 0.8, size * 0.8));
        this.beacons.setMatrixAt(lit, this.m4);
        this.m4.compose(bar, camQ, this.vS.set(size, size, size));
        this.flares.setMatrixAt(lit, this.m4);
        lit++;
      }
      for (const id of this.yaw.keys()) if (!r.groomers.has(id)) this.yaw.delete(id);
    }
    this.body.end();
    for (const m of [this.front, this.rear, this.beacons, this.flares]) {
      m.count = lit;
      m.visible = lit > 0;
      if (lit > 0) m.instanceMatrix.needsUpdate = true;
    }
  }

  stats(): { instances: number; triangles: number } {
    return { instances: this.body.instanceCount, triangles: this.body.triangleCount };
  }
}